import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBlog } from "../context/BlogContext";

function LoginForm() {
  const { login } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const onSubmit = (e) => {
    e.preventDefault();
    const ok = login(password);
    if (!ok) {
      setError("Incorrect password. Try the sample password: daydream");
    }
  };

  return (
    <div className="page admin-page">
      <header className="page-header">
        <h1>Admin</h1>
        <p>Sign in to manage blog posts. Sample password: <code>daydream</code></p>
      </header>

      <form className="admin-form panel" onSubmit={onSubmit}>
        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError("");
          }}
          autoComplete="current-password"
          required
        />
        {error && <p className="form-error">{error}</p>}
        <button type="submit" className="btn btn-primary">
          Sign in
        </button>
      </form>
    </div>
  );
}

function emptyDraft() {
  return { title: "", excerpt: "", body: "", date: new Date().toISOString().slice(0, 10) };
}

function AdminDashboard() {
  const { logout } = useAuth();
  const { posts, addPost, updatePost, deletePost } = useBlog();
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");

  const startEdit = (post) => {
    setEditingId(post.id);
    setDraft({
      title: post.title,
      excerpt: post.excerpt,
      body: post.body,
      date: post.date,
    });
    setMessage("");
  };

  const reset = () => {
    setEditingId(null);
    setDraft(emptyDraft());
    setMessage("");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!draft.title.trim() || !draft.body.trim()) {
      setMessage("Title and body are required.");
      return;
    }

    if (editingId) {
      updatePost(editingId, draft);
      setMessage("Post updated.");
    } else {
      const created = addPost(draft);
      setMessage(`Published “${created.title}”.`);
    }
    reset();
  };

  return (
    <div className="page admin-page">
      <header className="page-header admin-header">
        <div>
          <h1>Admin panel</h1>
          <p>Create, edit, or delete blog posts. Changes stay in this browser.</p>
        </div>
        <div className="admin-header-actions">
          <Link to="/blog" className="btn btn-ghost">
            View blog
          </Link>
          <button type="button" className="btn btn-ghost" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      <form className="admin-form panel" onSubmit={onSubmit}>
        <h2>{editingId ? "Edit post" : "New post"}</h2>
        <label htmlFor="title">Title</label>
        <input
          id="title"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          required
        />

        <label htmlFor="date">Date</label>
        <input
          id="date"
          type="date"
          value={draft.date}
          onChange={(e) => setDraft({ ...draft, date: e.target.value })}
        />

        <label htmlFor="excerpt">Excerpt</label>
        <input
          id="excerpt"
          value={draft.excerpt}
          onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
          placeholder="Optional short summary"
        />

        <label htmlFor="body">Body</label>
        <textarea
          id="body"
          rows={8}
          value={draft.body}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          required
        />

        {message && <p className="form-message">{message}</p>}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary">
            {editingId ? "Save changes" : "Publish"}
          </button>
          {editingId && (
            <button type="button" className="btn btn-ghost" onClick={reset}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <section className="admin-posts">
        <h2 className="section-title">Existing posts</h2>
        <ul className="admin-post-list">
          {posts.map((post) => (
            <li key={post.id} className="admin-post-row panel">
              <div>
                <strong>{post.title}</strong>
                <span>{post.date}</span>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => startEdit(post)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    if (window.confirm(`Delete “${post.title}”?`)) {
                      deletePost(post.id);
                      if (editingId === post.id) reset();
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export default function Admin() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <AdminDashboard /> : <LoginForm />;
}
